/**
 * Which add-ons may go with a DPO (same rules as the server, pioneer_lib.php):
 * an A-la-carte channel / Broadcaster pack can't be added when any of its channels is
 * already in the DPO, in FTA, or in another chosen add-on.
 * A pack whose channel list was never synced (channel_ids === null) is not checked.
 */

export const MONTH_DAYS = 30;

const channelName = (pack, id) => {
  const i = (pack.channel_ids || []).indexOf(id);
  return i >= 0 && pack.channels ? pack.channels[i] : `#${id}`;
};

/** Map channel_id -> owning pack name, from the base packs (DPO, FTA). */
export const ownerMap = (basePacks) => {
  const m = new Map();
  basePacks.filter(Boolean).forEach((p) => (p.channel_ids || []).forEach((id) => m.set(id, p)));
  return m;
};

const describe = (pack, clashes) => {
  // clashes: Map owner pack -> [channel names]
  const parts = [];
  clashes.forEach((names, owner) => {
    const shown = names.slice(0, 3).join(", ") + (names.length > 3 ? ` +${names.length - 3}` : "");
    parts.push(`${shown} already in ${owner.name}`);
  });
  return parts.join("; ");
};

/**
 * Why `pack` can't be ticked right now, or null.
 * basePacks: [dpo, fta]   selected: array of chosen add-on packs (not including `pack`)
 */
export const blockReason = (pack, basePacks, selected) => {
  if (!pack.channel_ids) return null;
  const owners = ownerMap(basePacks);
  selected.forEach((s) => {
    if (s.id !== pack.id) (s.channel_ids || []).forEach((id) => { if (!owners.has(id)) owners.set(id, s); });
  });
  const clashes = new Map();
  pack.channel_ids.forEach((id) => {
    const o = owners.get(id);
    if (o) {
      if (!clashes.has(o)) clashes.set(o, []);
      clashes.get(o).push(channelName(pack, id));
    }
  });
  return clashes.size ? describe(pack, clashes) : null;
};

/**
 * Chosen add-ons that clash (e.g. after the DPO was changed): Map id -> reason.
 * Checked in the order they were chosen, so the later one of two clashing add-ons is flagged.
 */
export const selectedConflicts = (basePacks, selected) => {
  const out = new Map();
  const kept = [];
  selected.forEach((p) => {
    const why = blockReason(p, basePacks, kept);
    if (why) out.set(p.id, why);
    else kept.push(p);
  });
  return out;
};

/** "Y-m-d H:i:s" | ISO -> Date | null */
export const parseDate = (v) => {
  if (!v) return null;
  const d = new Date(String(v).includes("T") ? v : String(v).replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? null : d;
};

export const fmtDate = (d) =>
  d ? d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

/** [start, end] of a recharge of `months` x 30 days (advance: from the day after `expiry`). */
export const periodOf = (months, advance, expiry) => {
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  const exp = parseDate(expiry);
  if (advance && exp) {
    start.setTime(exp.getTime());
    start.setHours(0, 0, 0, 0);
    start.setDate(start.getDate() + 1);
  }
  const end = new Date(start);
  end.setDate(end.getDate() + months * MONTH_DAYS - 1);
  return [start, end];
};

export const rupees = (v) => {
  const n = Number(v);
  return "₹" + (Number.isFinite(n) ? n : 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
};
